import { createReducer } from '@reduxjs/toolkit';
import { nearestStep } from '@shared/mathUtils.js';
import {
  gradientKey,
  PANORAMA_GRADIENT_DEFAULT,
  type PanoramaGradient,
  type PanoramaGradientStop,
  rememberedGradients,
} from '../gradient.js';
import { PROMINENCE_WEIGHT } from '../labels/fromPeaks.js';
import type { PanoramaDetail } from '../quality.js';
import { panoramaSetSettings } from './actions.js';

/**
 * How crowded the picture may get, as a bare level rather than a threshold:
 * what ranks a peak weighs its dominance against its distance and so has no
 * unit to set a cut in, and what a view offers depends entirely on where the
 * user stood. Ranking by it and taking the best few that fit is what the
 * measure is good for.
 */

/** The busiest setting; `0` is no names at all. */
export const LABEL_DENSITY_MAX = 10;

export type LabelLayoutLimits = {
  /** Viewport width one name may claim; absent means as many as will fit. */
  pitchPx?: number;
  maxClimbPx: number;
};

/**
 * What a density setting means to the layout, or `null` for no names at all.
 *
 * Both limits move together, because either alone is the one that binds and the
 * other then does nothing — a skyline offers far more peaks than fit on one
 * line, so what actually decides the count is how many lines they may stack
 * into. Geometric rather than linear: the difference between 140 px and 120 px
 * of elbow room is nothing, while 40 px to 20 px is a different picture.
 *
 * The busiest setting drops the width limit entirely: the most there can be is
 * however many the picture will physically hold, which is what asking for the
 * most ought to mean.
 */
export function labelLayoutLimits(level: number): LabelLayoutLimits | null {
  if (level <= 0) {
    return null;
  }

  const t = (Math.min(level, LABEL_DENSITY_MAX) - 1) / (LABEL_DENSITY_MAX - 1);

  return {
    ...(level >= LABEL_DENSITY_MAX
      ? {}
      : { pitchPx: Math.round(140 * (24 / 140) ** t) }),
    maxClimbPx: Math.round(24 * (208 / 24) ** t),
  };
}

/**
 * The step that filters nothing. A real number rather than `-Infinity`, which
 * would not survive being stored; deeper than any terrain, so it keeps even the
 * most subordinate top.
 */
export const NO_DOMINANCE_FILTER = -100_000;

/**
 * Farthest the haze slider reaches — the API's own ceiling for `range`, so
 * nothing can be rendered for the falloff to act on past it. At this end the
 * falloff does nothing, which is what `0` is for.
 */
export const LABEL_HAZE_MAX_KM = 400;

/**
 * The stops that slider has, with clear air **last**. `0` is no falloff and no
 * cut — names carrying further than any figure on the slider — so it belongs
 * past 400 km, not before 10: everywhere else the slider reads "further to the
 * right", and a `0` at the left end would break that exactly where it means the
 * opposite of what its position says.
 */
export const LABEL_HAZE_STEPS_KM = [
  ...Array.from({ length: LABEL_HAZE_MAX_KM / 10 }, (_, i) => (i + 1) * 10),
  0,
];

/** Which stop a stored haze stands on; anything at or below `0` is clear air. */
export function hazeStepIndex(km: number): number {
  return km > 0
    ? nearestStep(LABEL_HAZE_STEPS_KM.slice(0, -1), km)
    : LABEL_HAZE_STEPS_KM.length - 1;
}

/**
 * How far the picture may see, as the slider's stops in kilometres. The API's
 * own bounds are 1–400 km; the near end starts where a view of nothing but the
 * next hillside stops being one.
 */
export const RANGE_MIN_KM = 10;

export const RANGE_MAX_KM = 400;

/**
 * How hard distance is weighed against a summit's own metres, as the exponent
 * `p` in `dominance / distance ** p`. `0` is a summit's real size and `1` the
 * angle it subtends — the two a rank can be read as measuring, and `1` sits in
 * the middle of the scale because weighing both equally is what it does. Past
 * it the exponent measures nothing and is simply a preference for what is
 * close, which keeps earning its keep: from one viewpoint `1` names 15 summits
 * past 40 km and `2` names 9. It ends at `2` because the near field runs out,
 * not the exponent. See `doc/panorama.md`.
 */
export const LABEL_DISTANCE_WEIGHTS = [
  0, 0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2,
];

/**
 * Which of the five words describes a weight. The three stops a word is exactly
 * true of get it to themselves — `0` size, `1` both, `2` nearness — and the
 * quarters between take a "mostly". Worded in bands rather than per stop, the
 * way the density slider is: nine positions have no nine distinct words between
 * them, and inventing them gives a ladder with no top.
 */
export function labelWeightBand(weight: number): number {
  return weight <= 0
    ? 0
    : weight < 1
      ? 1
      : weight === 1
        ? 2
        : weight < 2
          ? 3
          : 4;
}

export const LABEL_DISTANCE_WEIGHT_MAX = LABEL_DISTANCE_WEIGHTS.at(-1) ?? 1;

/**
 * Where the prominence slider stops. Measured over four viewpoints, the effect
 * saturates around 0.6 — past it the same distant giants keep winning — so a
 * ceiling of `1` is already generous.
 */
export const PROMINENCE_WEIGHT_MAX = 1;

/** How fine that slider is; the default sits on the grid. */
export const PROMINENCE_WEIGHT_STEP = 0.05;

/**
 * A stored weight on the slider's own grid. The other three controls in that
 * menu snap through `nearestStep` or a band for the same reason: a value from
 * between the steps — an older store, a retuned default — otherwise leaves the
 * knob and the figure beside it disagreeing.
 */
export function prominenceWeightStep(weight: number): number {
  // Rounded to the step's own precision, or the multiply puts the error back:
  // `Math.round(0.3 / 0.05) * 0.05` is 0.30000000000000004, which is not a
  // valid `<input type=range>` step from `min` and never equals the default.
  return Number(
    (
      Math.round(
        Math.min(Math.max(weight, 0), PROMINENCE_WEIGHT_MAX) /
          PROMINENCE_WEIGHT_STEP,
      ) * PROMINENCE_WEIGHT_STEP
    ).toFixed(2),
  );
}

/**
 * Metres of dominance a summit needs before it is a candidate at all, as the
 * stops the slider has. Dominance is signed — a top that never rises clear of
 * its own ridge scores how far the ridge stands over it — so the negative stops
 * are real settings: they keep the tops that merely sit under their ridge while
 * dropping the bumps buried deep inside a massif. `0` is "only what stands
 * clear of its surroundings", which is what most people mean by a peak.
 *
 * Stops rather than a continuous range, because the useful values are spread
 * over three orders of magnitude: a linear slider across them would spend
 * nearly all its travel on the part nobody wants, and a logarithmic one cannot
 * cross zero. A 1-2-3-5-7 series per decade, each way from zero, gives a slider
 * fine enough to feel continuous while every stop it lands on is a round number
 * — nobody wants a threshold of 137 m.
 *
 * This and the density are two different questions — which summits count, and
 * how many of them fit — and both are answered here rather than in the request:
 * the service is always asked for everything it will name, because narrowing
 * the ask can only take candidates away and would cost a whole render to
 * change, while a filter over what already arrived is instant.
 */
const DOMINANCE_MAGNITUDES_M = [1, 2, 3, 5, 7].flatMap((mantissa) =>
  [1, 10, 100].map((decade) => mantissa * decade),
);

export const DOMINANCE_STEPS_M = [
  NO_DOMINANCE_FILTER,
  ...DOMINANCE_MAGNITUDES_M.map((m) => -m),
  0,
  ...DOMINANCE_MAGNITUDES_M,
  1000,
].sort((a, b) => a - b);

export type PanoramaTilt = 'standard' | 'wide' | 'flat' | 'custom';

/** A full turn — the fov that has no direction to aim and never runs out. */
export const FOV_FULL = 360;

/**
 * Slices of horizon to render, widest first. Render time is about linear in the
 * fov, so a narrow one buys back most of what a finer tier costs; the direction
 * it is aimed in is `panorama.renderAz`.
 *
 * A single number with a handful of useful values, so the list is the whole
 * control — unlike the tilt's pair, which needs the modal to type.
 */
export const PANORAMA_FOVS = [FOV_FULL, 180, 120, 90, 60, 30] as const;

/** Narrowest and widest a stored or linked fov may be. */
export const FOV_MIN = 5;

/**
 * A degree of slack, because this is asked of `meta.fov` as well as of the
 * setting: a step that does not divide the turn leaves the service's own
 * `width × step` a hair under 360, and reading that as a slice would stop the
 * picture wrapping. No preset comes near the gap.
 */
export function isFullTurn(fovDeg: number): boolean {
  return fovDeg >= FOV_FULL - 1;
}

/** Angles above and below the horizon a band may reach; short of straight up. */
export const ALT_LIMIT = 89;

/**
 * Where the unfolding slider stops, short of the service's own 45: the lift
 * raises the horizon by its own degrees, and the band raised with it costs
 * pixels, so a caricature past this would buy nothing but render time.
 */
export const DEPTH_LIFT_MAX = 20;

/** Degrees below and above horizontal each tilt preset frames. */
export const PANORAMA_TILTS: Record<
  Exclude<PanoramaTilt, 'custom'>,
  [number, number]
> = {
  standard: [-18, 12],
  wide: [-30, 25],
  flat: [-6, 6],
};

/**
 * The frame a settings state asks for, whichever way it says it. Here beside
 * the presets and the other settings derivations rather than in `quality.ts`,
 * which answers what a tier costs: the framing is a setting, and what reads it
 * is the menu, the modal and the URL codec.
 */
export function tiltRange(settings: PanoramaSettingsState): [number, number] {
  return settings.tilt === 'custom'
    ? [settings.altMin, settings.altMax]
    : PANORAMA_TILTS[settings.tilt];
}

/**
 * What a picture is drawn to look like: the ridge silhouettes' alpha gain and
 * thickness, what they are inked in, and the ground — one colour washed towards
 * the sky by the built-in haze, or a ramp that replaces both.
 */
export type PanoramaStyle = {
  ridgeStrength: number;
  ridgeWidth: number;
  ridgeColor: string;
  groundColor: string;
  /** Distance-to-colour ramp; `null` leaves `groundColor` and the haze. */
  groundGradient: PanoramaGradient | null;
};

/** What the service draws when asked for nothing — the flat ground the named
 * looks are built on, not what the panorama ships with. */
export const PANORAMA_STYLE_DEFAULTS: PanoramaStyle = {
  ridgeStrength: 1,
  ridgeWidth: 1,
  ridgeColor: '#000000',
  groundColor: '#3a4a34',
  groundGradient: null,
};

/**
 * Where the strength slider stops. The service has no ceiling — it is a gain on
 * an alpha that clamps at composite — but by about here even the haziest
 * distant ridge has saturated, so anything past it moves nothing.
 */
export const RIDGE_STRENGTH_MAX = 7;

/** The service's own bound: every stroke inks a band of rows, so width costs. */
export const RIDGE_WIDTH_MAX = 20;

/**
 * Named looks, so the usual answer is one press rather than three numbers
 * chosen blind — the only preview a colour has is a whole render.
 *
 * `natural` is what the panorama ships with, and the only one carrying a ramp:
 * the rest are the API's worked examples over the service's flat ground — no
 * linework at all, an inked map-like one, and a light ground under dark ink —
 * and a ramp beside them would ignore the very colour they are about.
 */
export const PANORAMA_LOOKS = {
  natural: {
    ...PANORAMA_STYLE_DEFAULTS,
    groundGradient: PANORAMA_GRADIENT_DEFAULT,
  },
  relief: { ...PANORAMA_STYLE_DEFAULTS, ridgeStrength: 0 },
  // A heavier stroke than the API's example asks for: what separates this from
  // the default is meant to read as drawn, and the width is the half of that
  // the alpha gain can't do.
  drawn: {
    ...PANORAMA_STYLE_DEFAULTS,
    ridgeStrength: 2.2,
    ridgeWidth: 1.6,
    groundColor: '#7a6a4a',
  },
  engraved: {
    ...PANORAMA_STYLE_DEFAULTS,
    ridgeColor: '#2b1a10',
    groundColor: '#d8cfc0',
  },
  /** Whatever the user set by hand; matched by nothing, so it is `null` here. */
  custom: null,
} satisfies Record<string, PanoramaStyle | null>;

/**
 * The looks by name. Derived from the constant rather than declared beside it,
 * so a look added or renamed without a label to go with it is a `tsc` error
 * rather than a menu item that silently renders blank.
 */
export type PanoramaLook = keyof typeof PANORAMA_LOOKS;

export const PANORAMA_LOOK_NAMES = Object.keys(
  PANORAMA_LOOKS,
) as PanoramaLook[];

/**
 * What a picture is drawn to look like, as one comparable string. Whichever of
 * the ground colour and the ramp the request will carry, never both, so a
 * colour left standing under a ramp that ignores it says nothing.
 */
export function panoramaStyleKey(style: PanoramaStyle): string {
  return [
    style.ridgeStrength,
    style.ridgeWidth,
    style.ridgeColor,
    style.groundGradient
      ? gradientKey(style.groundGradient)
      : style.groundColor,
  ].join('/');
}

/**
 * Which named look the current settings are, or `custom` where none matches.
 * By key rather than field by field: a ramp is a reference, so `===` would make
 * every look with one `custom` for ever, and silently.
 */
export function panoramaLookOf(style: PanoramaStyle): PanoramaLook {
  const key = panoramaStyleKey(style);

  return (
    PANORAMA_LOOK_NAMES.find((name) => {
      const look = PANORAMA_LOOKS[name];

      return look && panoramaStyleKey(look) === key;
    }) ?? 'custom'
  );
}

/** What a summit's second line can say, in the order it says them. */
export const SUB_LINE_PARTS = [
  'ele',
  'distance',
  'prominence',
  'isolation',
] as const;

export type SubLinePart = (typeof SUB_LINE_PARTS)[number];

/** The setting that turns each part on. */
export const SUB_LINE_KEY = {
  ele: 'showLabelEle',
  distance: 'showLabelDistance',
  prominence: 'showLabelProminence',
  isolation: 'showLabelIsolation',
} as const satisfies Record<SubLinePart, keyof PanoramaSettingsState>;

/** The parts switched on, in order. */
export function subLineOf(settings: PanoramaSettingsState): SubLinePart[] {
  return SUB_LINE_PARTS.filter((part) => settings[SUB_LINE_KEY[part]]);
}

export interface PanoramaSettingsState {
  /**
   * How fine a picture to ask for, pixels per degree, or `max` for whatever
   * the frame allows — which moves with the fov and the band. What an account
   * may have of it is decided by `grantedPanorama`, not here.
   */
  detail: PanoramaDetail;
  /** How much sky and ground the frame holds; `custom` uses the two below. */
  tilt: PanoramaTilt;
  altMin: number;
  altMax: number;
  /**
   * Degrees of horizon to render. Below a full turn the picture has ends, so
   * which way it faces becomes something to ask for — see `panorama.renderAz`.
   */
  fovDeg: number;
  /** Eye height above the ground, metres. */
  eye: number;
  /**
   * Degrees far terrain is raised by, unfolding the distance the projection
   * compresses; `0` is a true view. The lift decides what hides what, so it
   * draws summits the eye cannot see — hence `showRevealedLabels`.
   */
  depthLift: number;
  /**
   * Farthest terrain the picture holds, kilometres. Past `FREE_RANGE_MAX_KM` it
   * is premium's — every extra kilometre is samples on every ray.
   */
  rangeKm: number;
  /**
   * Whether the summits only the lift brings into view are named. A filter over
   * the labels in hand, so it is instant; the lift itself costs a render.
   */
  showRevealedLabels: boolean;
  /** How the picture is drawn; see `PANORAMA_LOOKS` and `PanoramaStyle`. */
  ridgeStrength: number;
  ridgeWidth: number;
  ridgeColor: string;
  groundColor: string;
  groundGradient: PanoramaGradient | null;
  /**
   * Ramps the user has saved, freshest first — the picker offers them beside
   * the built-in swatches. Colours only, which is all a swatch is.
   */
  recentGradients: PanoramaGradientStop[][];
  /** How many names to draw, `0` for none; see {@link labelLayoutLimits}. */
  labelDensity: number;
  /**
   * What each name's second line says; see {@link SUB_LINE_PARTS}. Off by
   * default: a two-line label is taller, so fewer names fit the picture.
   */
  showLabelEle: boolean;
  showLabelDistance: boolean;
  showLabelProminence: boolean;
  showLabelIsolation: boolean;
  /** Metres of dominance a summit needs to be named; see {@link DOMINANCE_STEPS_M}. */
  minDominance: number;
  /**
   * How far a name has to carry: both the falloff the rank is weighed by and,
   * a few times further out, where names stop altogether. `0` is clear air —
   * no falloff and no cut. See `hazeCutoffM`.
   */
  labelHazeKm: number;
  /** What the rank measures; see {@link LABEL_DISTANCE_WEIGHTS}. */
  labelDistanceWeight: number;
  /**
   * What a metre of true prominence is worth beside a metre of dominance —
   * whether a summit earns its name by being a mountain at all or by standing
   * out from here.
   */
  prominenceWeight: number;
  /**
   * Lets the view move on its own: toward where the device points where there
   * is a compass to ask, and at a steady turn where there is not. Turning the
   * picture by hand clears it, the same as the stop button does.
   */
  autoPan: boolean;
  /** Whether the sun's and the moon's paths over the day are drawn. */
  showSun: boolean;
  showMoon: boolean;
}

/**
 * A phone is held, not pointed with a mouse, so it turns by itself until the
 * user takes over. A stored preference wins over this.
 */
function touchDevice(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(pointer: coarse)').matches
  );
}

export const panoramaSettingsInitialState: PanoramaSettingsState = {
  // What to ask for, not what will be granted: an account without premium is
  // held to what its ray budget buys before the request goes out. Middling
  // rather than the free figure, which would leave a premium user on the coarse
  // picture until they found the control — and rather than `max`, which is the
  // better part of a minute of a server that renders one at a time.
  detail: 20,
  tilt: 'standard',
  altMin: -18,
  altMax: 12,
  // A full turn, which is what a panorama means and the only fov with nothing
  // to aim. The narrow ones are for a known view, and are asked for.
  fovDeg: FOV_FULL,
  eye: 1.7,
  depthLift: 0,
  // The service's own default, and what every render asked for before there was
  // a control: free accounts see no change.
  rangeKm: 300,
  showRevealedLabels: true,
  ...PANORAMA_LOOKS.natural,
  recentGradients: [],
  labelDensity: 5,
  showLabelEle: false,
  showLabelDistance: false,
  showLabelProminence: false,
  showLabelIsolation: false,
  minDominance: NO_DOMINANCE_FILTER,
  labelHazeKm: 120,
  labelDistanceWeight: 0.5,
  prominenceWeight: PROMINENCE_WEIGHT,
  autoPan: touchDevice(),
  showSun: true,
  showMoon: false,
};

export const panoramaSettingsReducer = createReducer(
  panoramaSettingsInitialState,
  (builder) =>
    builder.addCase(panoramaSetSettings, (state, { payload }) => {
      Object.assign(state, payload);

      // Saving the settings is the deliberate act — everything before it is the
      // modal's own draft — so it is where a ramp the user built is kept.
      if (payload.groundGradient) {
        state.recentGradients = rememberedGradients(
          state.recentGradients,
          payload.groundGradient.stops,
        );
      }
    }),
);
